import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { logger } from '@/lib/logger';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
  componentName?: string;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  componentName = 'Unknown Component'
}) => {
  const errorLogger = logger.createComponentLogger(`ErrorFallback-${componentName}`);

  React.useEffect(() => {
    errorLogger.error('Component error boundary triggered', error);
  }, [error, errorLogger]);

  const handleRetry = () => {
    errorLogger.info('User initiated error recovery');
    resetErrorBoundary();
  };

  return (
    <Card className="max-w-md mx-auto border-destructive/20 bg-destructive/5">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-2">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>
        <CardTitle className="text-destructive">Something went wrong</CardTitle>
        <CardDescription>
          An error occurred in {componentName}. This has been logged for investigation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {process.env.NODE_ENV === 'development' && (
          <details className="text-xs bg-muted p-2 rounded">
            <summary className="cursor-pointer font-medium">Error Details</summary>
            <pre className="mt-2 whitespace-pre-wrap">{error.message}</pre>
            {error.stack && (
              <pre className="mt-2 text-xs opacity-75">{error.stack}</pre>
            )}
          </details>
        )}
        <Button 
          onClick={handleRetry} 
          className="w-full"
          variant="outline"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </CardContent>
    </Card>
  );
};